import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import smartLombardService from '../services/smartlombard.service';
import type { SmartLombardInstance } from '../types/smartlombard.types';
import { usePermissions } from '../utils/usePermissions';
import { useAppFeatures } from './AppFeaturesContext';
import { safeGetItem, safeSetItem, safeRemoveItem, safeGetJson, safeSetJson } from '../utils/safeStorage';

interface SmartLombardContextType {
  instances: SmartLombardInstance[];
  currentInstance: SmartLombardInstance | null;
  isLoading: boolean;
  error: string | null;
  setCurrentInstance: (instance: SmartLombardInstance | null) => void;
  refreshInstances: () => Promise<void>;
}

const SmartLombardContext = createContext<SmartLombardContextType | null>(null);

const STORAGE_KEY = 'smartlombard_selected_instance_id';
const STORAGE_KEY_DATA = 'smartlombard_selected_instance_data';

const readStoredInstance = (): SmartLombardInstance | null => {
  if (typeof window === 'undefined') return null;
  const parsed = safeGetJson<SmartLombardInstance | null>(STORAGE_KEY_DATA, null);
  if (
    parsed !== null &&
    typeof parsed === 'object' &&
    typeof parsed.id === 'number' &&
    typeof parsed.name === 'string' &&
    typeof parsed.is_active === 'boolean' &&
    typeof parsed.base_url === 'string'
  ) {
    return parsed;
  }
  return null;
};

export const SmartLombardProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [instances, setInstances] = useState<SmartLombardInstance[]>([]);
  const [currentInstance, setCurrentInstanceState] = useState<SmartLombardInstance | null>(() => readStoredInstance());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { hasAny } = usePermissions();
  const appFeatures = useAppFeatures();
  const canListInstances = hasAny([
    'integrations:smartlombard:pawn_tickets:list',
    'integrations:smartlombard:pawn_tickets:read',
    'integrations:smartlombard:*',
  ]);

  const loadInstances = useCallback(async () => {
    if (!appFeatures.smartlombard) {
      setInstances([]);
      setCurrentInstanceState(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    if (!canListInstances) {
      setInstances((prev) => (prev.length ? [] : prev));
      setCurrentInstanceState((prev) => (prev ? null : prev));
      setError((prev) => (prev === 'Недостаточно прав для просмотра инстансов SmartLombard' ? prev : 'Недостаточно прав для просмотра инстансов SmartLombard'));
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await smartLombardService.getInstances({ is_active: true });
      setInstances(data);

      let selected: SmartLombardInstance | null = null;

      if (data.length > 0) {
        const savedId = safeGetItem(STORAGE_KEY);
        if (savedId) {
          const savedInstance = data.find(inst => inst.id === Number(savedId) && inst.is_active);
          if (savedInstance) {
            selected = savedInstance;
          }
        }

        if (!selected) {
          selected = data.find(inst => inst.is_active) || data[0];
        }
      }

      setCurrentInstanceState(selected);

      if (selected) {
        safeSetItem(STORAGE_KEY, String(selected.id));
        safeSetJson(STORAGE_KEY_DATA, selected);
      } else {
        safeRemoveItem(STORAGE_KEY);
        safeRemoveItem(STORAGE_KEY_DATA);
      }
    } catch (err: any) {
      console.error('[SmartLombardContext] Failed to load instances:', err);
      setError(err?.message || 'Не удалось загрузить список инстансов');
      setInstances([]);
      setCurrentInstanceState(null);
    } finally {
      setIsLoading(false);
    }
  }, [appFeatures.smartlombard, canListInstances]);

  useEffect(() => {
    loadInstances();
  }, [loadInstances]);

  const setCurrentInstance = useCallback((instance: SmartLombardInstance | null) => {
    setCurrentInstanceState(instance);
    if (instance) {
      safeSetItem(STORAGE_KEY, String(instance.id));
      safeSetJson(STORAGE_KEY_DATA, instance);
    } else {
      safeRemoveItem(STORAGE_KEY);
      safeRemoveItem(STORAGE_KEY_DATA);
    }
  }, []);

  const refreshInstances = useCallback(async () => {
    await loadInstances();
  }, [loadInstances]);

  const value = useMemo<SmartLombardContextType>(() => ({
    instances,
    currentInstance,
    isLoading,
    error,
    setCurrentInstance,
    refreshInstances,
  }), [instances, currentInstance, isLoading, error, setCurrentInstance, refreshInstances]);

  return (
    <SmartLombardContext.Provider value={value}>
      {children}
    </SmartLombardContext.Provider>
  );
};

export const useSmartLombard = (): SmartLombardContextType => {
  const context = useContext(SmartLombardContext);
  if (!context) {
    throw new Error('useSmartLombard must be used within SmartLombardProvider');
  }
  return context;
};
